import {
  QUALITY_ASSESSMENT_TEMPLATE_API_ROUTE,
  tokenizeRoute,
} from '../routes';
import { getCsrfToken } from '../index';
import { getReviewId } from './Review';
import {
  QualityAssessmentTemplate,
  QualityAssessmentTemplateFormat,
} from 'types/DataExtraction';

type QualityAssessmentTemplateAPI = {
  domains: {
    id?: number;
    name: string;
    description: string;
    outcome_specific: boolean;
    prompt_before_deleting: boolean;
    judgements?: {
      id?: number;
      label: string;
      position: number;
    }[];
  }[];
  outcome_groups: {
    id?: number;
    name: string;
    prompt_before_deleting: boolean;
  }[];
  updated_by?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  updated_at?: string;
  number_of_revisitable_studies: number;
};

export type getQATemplateProps = {
  initialFormat: QualityAssessmentTemplateFormat;
};

const DEFAULT_JUDGEMENTS = [
  { label: 'High', position: 1 },
  { label: 'Low', position: 2 },
  { label: 'Unclear', position: 3 },
];

const cloneDefaultJudgements = () =>
  DEFAULT_JUDGEMENTS.map((judgement) => ({ ...judgement }));

const getQATemplate = async ({
  initialFormat,
}: getQATemplateProps): Promise<QualityAssessmentTemplate> => {
  const reviewId = getReviewId();
  if (!reviewId) throw new Error("Couldn't get Review ID.");

  const url = tokenizeRoute(QUALITY_ASSESSMENT_TEMPLATE_API_ROUTE, {
    review_id: reviewId,
    initial_format: initialFormat,
  });

  return fetch(url.toString(), {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  })
    .then((res) => res.json())
    .then((res: QualityAssessmentTemplateAPI) => {
      const {
        domains = [],
        outcome_groups = [],
        updated_by,
        updated_at,
        number_of_revisitable_studies,
      } = res;
      // Always seed default judgement labels for domains that have none.
      // The feature flag controls UI visibility only; judgement data is always
      // stored at the domain level so toggling the flag never loses custom labels.
      const domainsWithJudgements = domains.map((domain) => {
        if (domain.judgements && domain.judgements.length > 0) {
          return domain;
        }
        // Ensure each domain receives independent judgement objects.
        // This avoids accidental cross-domain mutations if code later mutates in place.
        return { ...domain, judgements: cloneDefaultJudgements() };
      });
      return {
        domains: domainsWithJudgements,
        outcome_groups,
        updated_by,
        updated_at,
        number_of_revisitable_studies,
      };
    });
};

const updateQATemplate = (
  { domains, outcome_groups }: QualityAssessmentTemplate,
  revisitStudies: boolean
) => {
  const body = {
    domains,
    outcome_groups,
    revisit_studies: revisitStudies,
  };

  const reviewId = getReviewId();
  if (!reviewId) throw new Error("Couldn't get Review ID.");

  const url = tokenizeRoute(QUALITY_ASSESSMENT_TEMPLATE_API_ROUTE, {
    review_id: reviewId,
  });

  return fetch(url.toString(), {
    method: 'PUT',
    headers: {
      'X-CSRF-Token': getCsrfToken(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
};

const createEmptyQATemplate = () => {
  return {
    domains: [],
    outcome_groups: [],
    number_of_revisitable_studies: 0,
  };
};

export { getQATemplate, updateQATemplate, createEmptyQATemplate };
